/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import DS from 'ember-data';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import EnvironmentService from 'cio/services/environment';
import { tracked } from '@glimmer/tracking';
import CustomForm, { CustomFormAttrMap } from 'cio/models/custom-form';
import AuthService from 'cio/services/auth';
import { IdentifierKey } from 'cio/utils/identifiers';
import { RESERVED_FORM_FIELD_NAMES } from 'cio/utils/forms';

interface FormsCustomFormSettingsMapFormFieldsArgs {
  form: CustomForm;
  isDetailsPage?: boolean;
  isDisabled?: boolean;
}

interface AttrMapForForm {
  formFieldKey: string;
  formFieldLabel: string;
  formFieldType: string;
  cioAttr: string;
  isIdentifier: boolean;
  checked: boolean;
  shouldValidateKey: boolean;
  shouldValidateAttr: boolean;
  uuid: string; // used for rendering purposes (#each loop key)
}

export default class FormsCustomFormSettingsMapFormFields extends Component<FormsCustomFormSettingsMapFormFieldsArgs> {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  // Form fields are displayed with their key as labels (formFieldKey)
  // Except for Typeform forms: where field keys are unreadable, so we allow toggling to display actual field labels instead (formFieldLabel)
  @tracked attrMapLabelKeyToDisplay: 'formFieldKey' | 'formFieldLabel' = 'formFieldKey';

  @tracked attrMapsForForm!: AttrMapForForm[];

  constructor(owner: unknown, args: FormsCustomFormSettingsMapFormFieldsArgs) {
    super(owner, args);

    this.attrMapLabelKeyToDisplay = this.args.form.isTypeform ? 'formFieldLabel' : 'formFieldKey';
  }

  private isAttrMapIdentifier(attrMap: CustomFormAttrMap) {
    return (this.environment.current.enabledIdentifiers as string[]).includes(attrMap.cioAttr);
  }

  @action
  setAttrMapsForForm() {
    let { attrMaps } = this.args.form;

    this.attrMapsForForm = attrMaps.map((fMap) => ({
      formFieldKey: fMap.formFieldKey,
      formFieldLabel: fMap.formFieldLabel,
      formFieldType: fMap.formFieldType,
      cioAttr: fMap.cioAttr,
      isIdentifier: this.isAttrMapIdentifier(fMap),
      checked: Boolean(fMap.cioAttr),

      // show validation errors for existing mappings, if present (API submission without valid identifier scenario)
      shouldValidateKey: true,
      shouldValidateAttr: true,

      uuid: window.UUID.generate(),
    }));
  }

  get selectableAttributes() {
    return this.environment.sentAttributes.without('cio_id');
  }

  get attrMapsForFormFieldKeyErrors() {
    return this.attrMapsForForm.map((m) => {
      if (!m.shouldValidateKey) {
        return [];
      }

      if (m.checked && m.formFieldKey.trim() === '') {
        return ['Empty form field.'];
      }

      if (RESERVED_FORM_FIELD_NAMES.includes(m.formFieldKey.trim())) {
        return ['Reserved field name.'];
      }

      let attrMapsUsingThisFieldKey = this.args.form.attrMaps.filter(
        (f) => f.formFieldKey.trim() === m.formFieldKey.trim(),
      );

      if (attrMapsUsingThisFieldKey.length > 1) {
        return ['Duplicate form field.'];
      }

      return [];
    });
  }

  get attrMapsForFormCioAttrErrors() {
    return this.attrMapsForForm.map((m) => {
      if (!m.shouldValidateAttr) {
        return [];
      }

      if (this.args.form.isScannedForm && m.checked && m.cioAttr.trim() === '') {
        return ['Empty person attribute'];
      }

      if (m.cioAttr.trim() === IdentifierKey.CioId) {
        return [`You cannot use ${IdentifierKey.CioId}`];
      }

      let attrMapsUsingThisCioAttr = this.args.form.attrMaps.filter((f) => f.cioAttr.trim() === m.cioAttr.trim());
      if (m.cioAttr.trim() !== '' && attrMapsUsingThisCioAttr.length > 1) {
        return ['Already mapped to another profile attribute.'];
      }

      let isIdentifier = (this.environment.current.enabledIdentifiers as string[]).includes(m.cioAttr.trim());
      if (isIdentifier && this.args.form.identifierAttrMaps.length > 1) {
        return ['Can map to only one identifier attribute (email or id)'];
      }

      return [];
    });
  }

  get canChangeField() {
    if (this.args.form.get('isNew')) {
      return this.auth.hasPermission('journeys/forms/create');
    }

    return this.auth.hasPermission('journeys/forms/edit');
  }

  // Un-checking a form field keeps its current value in the form, clears it on the underlying model:
  @action updateChecked(index: number, checked: boolean) {
    let attrMapForForm = this.attrMapsForForm[index];
    if (attrMapForForm == null) {
      return;
    }
    attrMapForForm = {
      ...attrMapForForm,
      checked,
    };
    this.updateAttrMapsForFormAtIndex(index, attrMapForForm);

    let originalIndex = this.args.form.attrMaps.findIndex((am) => am.formFieldKey === attrMapForForm.formFieldKey);
    if (originalIndex === -1) {
      return;
    }
    let attrMap = this.args.form.attrMaps[originalIndex];
    let clone = { ...attrMap, cioAttr: checked ? attrMapForForm.cioAttr : '' };
    this.args.form.updateAttrMapAtIndex(originalIndex, clone);
  }

  @action updateFormFieldKey(index: number, formFieldKey: string) {
    formFieldKey = formFieldKey.trim();

    let attrMapForForm = this.attrMapsForForm[index];
    if (attrMapForForm == null) {
      return;
    }
    attrMapForForm = {
      ...attrMapForForm,
      formFieldKey,
      shouldValidateKey: true,
    };
    this.updateAttrMapsForFormAtIndex(index, attrMapForForm);

    let attrMap = this.args.form.attrMaps[index];
    if (attrMap == null) {
      return;
    }
    let originalIndex = this.args.form.attrMaps.indexOf(attrMap);
    let clone = { ...attrMap, formFieldKey };
    this.args.form.updateAttrMapAtIndex(originalIndex, clone);
  }

  @action updateCioAttr(index: number, cioAttr: string) {
    cioAttr = cioAttr.trim();

    let attrMapForForm = this.attrMapsForForm[index];
    if (attrMapForForm == null) {
      return;
    }
    attrMapForForm = {
      ...attrMapForForm,
      cioAttr,
      shouldValidateAttr: true,

      // check if not checked, updated with valid cio_attr value:
      checked: cioAttr ? true : attrMapForForm.checked,
    };
    attrMapForForm.isIdentifier = this.isAttrMapIdentifier(attrMapForForm);
    this.updateAttrMapsForFormAtIndex(index, attrMapForForm);

    let attrMap = this.args.form.attrMaps.find((am) => am.formFieldKey === attrMapForForm.formFieldKey);

    if (attrMap == null) {
      return;
    }

    let originalIndex = this.args.form.attrMaps.indexOf(attrMap);
    let clone = { ...attrMap, cioAttr };
    this.args.form.updateAttrMapAtIndex(originalIndex, clone);
  }

  private updateAttrMapsForFormAtIndex(index: number, attrMapForForm: AttrMapForForm) {
    let attrMapsForForm = [...this.attrMapsForForm];
    attrMapsForForm[index] = attrMapForForm;
    this.attrMapsForForm = attrMapsForForm;
  }

  @action deleteField(index: number) {
    this.attrMapsForForm = this.attrMapsForForm.filter((_, i) => i !== index);
    this.args.form.attrMaps = this.args.form.attrMaps.filter((_, i) => i !== index);
  }

  @action addField() {
    this.attrMapsForForm = [
      ...this.attrMapsForForm,
      {
        formFieldKey: '',
        formFieldLabel: '',
        formFieldType: '',
        cioAttr: '',
        isIdentifier: false,
        checked: true,
        shouldValidateKey: false,
        shouldValidateAttr: false,
        uuid: window.UUID.generate(),
      },
    ];

    this.args.form.attrMaps = [
      ...this.args.form.attrMaps,
      {
        formFieldKey: '',
        formFieldLabel: '',
        formFieldType: '',
        cioAttr: '',
      },
    ];
  }
}
